import {
    type ApprovedWhatsAppTemplateParameters,
    WHATSAPP_TEMPLATE_PARAMETER_KINDS,
    WHATSAPP_TEMPLATE_VARIABLE_SOURCES,
    type WhatsAppVariableName,
} from 'cpool-constants';

import { NAMED_PLACEHOLDER_PATTERN, POSITIONAL_PLACEHOLDER_PATTERN } from './parseTemplateSegments';

export type TemplatePreviewSourceValues = Partial<Record<WhatsAppVariableName, string | null | undefined>>;

export type TemplatePreviewValues = TemplatePreviewSourceValues & {
    namedValues: Record<string, string>;
    positionalValues?: Array<string>;
};

type SubstituteTemplatePlaceholdersParams = {
    body: string;
    parameters: ApprovedWhatsAppTemplateParameters;
    resolveComputedPreviewValue?: (name: string) => string | undefined;
    values: TemplatePreviewValues;
};

// Best-effort preview substitution — leaves `{{…}}` tokens when a value isn't available.
export function substituteTemplatePlaceholders({
    body,
    parameters,
    resolveComputedPreviewValue,
    values,
}: SubstituteTemplatePlaceholdersParams): string {
    if (parameters.kind === WHATSAPP_TEMPLATE_PARAMETER_KINDS.NONE) return body;

    if (parameters.kind === WHATSAPP_TEMPLATE_PARAMETER_KINDS.POSITIONAL) {
        return body.replace(POSITIONAL_PLACEHOLDER_PATTERN, (fullMatch, index: string) => {
            const value = values.positionalValues?.[Number(index) - 1];
            return value?.trim() ? value : fullMatch;
        });
    }

    return body.replace(NAMED_PLACEHOLDER_PATTERN, (fullMatch, name: string) => {
        const variable = parameters.variables[name];
        if (!variable) return fullMatch;

        if (variable.source === WHATSAPP_TEMPLATE_VARIABLE_SOURCES.CUSTOM) {
            return values.namedValues[name] || fullMatch;
        }

        if (
            variable.source === WHATSAPP_TEMPLATE_VARIABLE_SOURCES.AGENT ||
            variable.source === WHATSAPP_TEMPLATE_VARIABLE_SOURCES.APPLICANT ||
            variable.source === WHATSAPP_TEMPLATE_VARIABLE_SOURCES.PARTNER
        ) {
            return values[name as WhatsAppVariableName] || fullMatch;
        }

        if (variable.source === WHATSAPP_TEMPLATE_VARIABLE_SOURCES.COMPUTED) {
            return resolveComputedPreviewValue?.(name) ?? fullMatch;
        }

        return fullMatch;
    });
}
